import { useCallback, useEffect, useRef, useState } from 'react';

// hooks
import { useRedux } from '../../../hooks/index';
import { createSelector } from 'reselect';
import useDebounce from '../../../hooks/useDebounce';
// components
import Loader from '../../../components/Loader';
import AppSimpleBar from '../../../components/AppSimpleBar';
import CreateContactModal from '../../../components/CreateContactModal';
import EmptyStateResult from '../../../components/EmptyStateResult';
import ListHeader from './ListHeader';
import Contact from './Contact';

// actions
import {
    createContact,
    resetContacts,
    getChannelDetails,
    getChatUserDetails,
    changeSelectedChat,
    editContact,
    deleteContact,
    getContacts,
    updateSearch,
    updateCurrentPage,
    getChatUserMessages,
    updateCurrentPageUserMessages
} from '../../../redux/actions';

//utils
import { divideByKey, DivideByKeyResultTypes } from '../../../utils';
import EditContactModal from '../../../components/EditContactModal';
import DeleteConfirmationModal from '../../../components/DeleteConfirmationModal';
import { useNavigate } from 'react-router-dom';
import { getChatUserConversationsByInstance } from '../../../api';

interface IndexProps {}

const Index = (props: IndexProps) => {
    const navigate = useNavigate();
    // global store
    const { dispatch, useAppSelector } = useRedux();

    const errorData = createSelector(
        (state: any) => state.Contacts,
        (props: any) => props.Chats,
        (loginState: any) => loginState.Login,
        (state, props, loginState) => ({
            contactsList: state.contacts,
            getContactsLoading: state.getContactsLoading,
            isContactInvited: state.isContactInvited,
            hasMoreContacts: state.hasMoreContacts,
            error: state.error,
            searchContact: state.search,
            currentPage: state.currentPage,
            chatUserDetails: props.chatUserDetails,
            selectedInstance: props.selectedInstance
        })
    );
    // Inside your component
    const {
        contactsList,
        getContactsLoading,
        isContactInvited,
        error,
        searchContact,
        currentPage,
        hasMoreContacts,
        chatUserDetails,
        selectedInstance
    } = useAppSelector(errorData);

    // get contacts
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search);

    const intersectionObserver = useRef<IntersectionObserver | null>(null);
    const lastContactElementRef = useCallback(
        (contact: any) => {
            if (getContactsLoading) return;
            if (intersectionObserver.current)
                intersectionObserver.current.disconnect();
            intersectionObserver.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && hasMoreContacts) {
                        dispatch(updateCurrentPage(currentPage + 1));
                    }
                }
            );
            if (contact) intersectionObserver.current.observe(contact);
        },
        [getContactsLoading, hasMoreContacts, currentPage, dispatch]
    );

    /* contact search */
    const onChangeSearch = (value: string) => {
        setSearch(value);
    };

    const [contacts, setContacts] = useState<Array<any>>([]);
    const [contactsData, setContactsData] = useState<Array<any>>([]);
    useEffect(() => {
        setContacts(contactsList);
    }, [contactsList]);

    useEffect(() => {
        if (contacts.length > 0) {
            const formattedContacts = divideByKey(
                'displayName',
                'firstName',
                contacts
            );
            setContactsData(formattedContacts);
        }
    }, [contacts]);

    /* add/edit/delete contact modal handeling */
    const [isCreateContactOpen, setIsCreateContactOpen] =
        useState<boolean>(false);
    const [isEditContactOpen, setIsEditContactOpen] = useState<boolean>(false);
    const [isDeleteContactOpen, setIsDeleteContactOpen] =
        useState<boolean>(false);

    const openCreateContactModal = () => {
        setIsCreateContactOpen(true);
    };
    const closeCreateContactModal = () => {
        setIsCreateContactOpen(false);
    };
    const openEditContactModal = () => {
        setIsEditContactOpen(true);
    };
    const closeEditContactModal = () => {
        setIsEditContactOpen(false);
    };
    const openDeleteContactModal = () => {
        setIsDeleteContactOpen(true);
    };
    const closeDeleteContactModal = () => {
        setIsDeleteContactOpen(false);
    };

    /* onCreate handeling */
    const onCreateContact = (data: any) => {
        dispatch(createContact(data));
        closeCreateContactModal();
    };

    /* onEdit handeling */
    const onEditContact = (data: any) => {
        dispatch(editContact(data, chatUserDetails.personId));
        closeEditContactModal();
    };

    /* onDelete handeling */
    const onDeleteContact = () => {
        dispatch(deleteContact(chatUserDetails.personId));
        dispatch(changeSelectedChat(null));
        closeDeleteContactModal();
        setSearch('');
        dispatch(updateCurrentPage(1));
    };

    useEffect(() => {
        if (isContactInvited) {
            setIsCreateContactOpen(false);
            setTimeout(() => {
                dispatch(resetContacts('isContactInvited', false));
            }, 1000);
        }
    }, [dispatch, isContactInvited]);

    const totalC = (contacts || []).length;
    const onSelectChat = async (personId: number, isChannel?: boolean) => {
        if (isChannel) {
            dispatch(getChannelDetails(personId));
        } else {
            dispatch(getChatUserDetails(personId));
        }
        try {
            const response = await getChatUserConversationsByInstance(
                personId,
                selectedInstance.value
            );
            const conversationId = response.data[0]
                ? response.data[0].conversationId
                : null;
            dispatch(
                getChatUserMessages(conversationId, {
                    page: 1
                })
            );
            dispatch(changeSelectedChat({ personId, conversationId }));
            dispatch(updateCurrentPageUserMessages(1));
        } catch (error) {}
    };

    useEffect(() => {
        if (error) {
            navigate('/logout');
            dispatch(resetContacts('isContactInvited', false));
        }
    }, [error, navigate, dispatch]);

    useEffect(() => {
        const fetchContacts = () => {
            dispatch(
                getContacts({
                    search: searchContact,
                    page: currentPage
                })
            );
        };
        fetchContacts();
    }, [searchContact, currentPage, dispatch]);

    useEffect(() => {
        dispatch(updateSearch(debouncedSearch));
    }, [debouncedSearch, dispatch]);

    return (
        <>
            <div className="position-relative">
                {getContactsLoading && <Loader />}
                <ListHeader
                    search={search}
                    onChangeSearch={onChangeSearch}
                    openModal={openCreateContactModal}
                />

                <AppSimpleBar className="chat-message-list chat-group-list">
                    <div>
                        {totalC === 0 ? (
                            <EmptyStateResult searchedText={search} />
                        ) : (
                            (contactsData || []).map(
                                (
                                    letterContacts: DivideByKeyResultTypes,
                                    key: number
                                ) => {
                                    if (contactsData.length === key + 1) {
                                        return (
                                            <Contact
                                                letterContacts={letterContacts}
                                                key={key}
                                                index={key}
                                                onSelectChat={onSelectChat}
                                                openEditModal={
                                                    openEditContactModal
                                                }
                                                openDeleteModal={
                                                    openDeleteContactModal
                                                }
                                                ref={lastContactElementRef}
                                            />
                                        );
                                    } else {
                                        return (
                                            <Contact
                                                letterContacts={letterContacts}
                                                key={key}
                                                index={key}
                                                onSelectChat={onSelectChat}
                                                openEditModal={
                                                    openEditContactModal
                                                }
                                                openDeleteModal={
                                                    openDeleteContactModal
                                                }
                                            />
                                        );
                                    }
                                }
                            )
                        )}
                    </div>
                </AppSimpleBar>
            </div>
            {isCreateContactOpen && (
                <CreateContactModal
                    isOpen={isCreateContactOpen}
                    onClose={closeCreateContactModal}
                    onCreate={onCreateContact}
                />
            )}

            {/* Edit contact modal */}
            {isEditContactOpen && (
                <EditContactModal
                    isOpen={isEditContactOpen}
                    onClose={closeEditContactModal}
                    onEdit={onEditContact}
                    chatUserDetails={chatUserDetails}
                />
            )}
            {/* Delete contact modal */}
            <DeleteConfirmationModal
                isOpen={isDeleteContactOpen}
                onClose={closeDeleteContactModal}
                onDelete={onDeleteContact}
            />
        </>
    );
};

export default Index;
